/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import { useApiContext } from 'context/ApiContext';
import type {
  AssignUserWarehouseRequest,
  CloneUserAccessElement,
  CloneUserAssignations,
  EditUseRequest
} from 'api/security';
import { useGlobalContext } from 'context/GlobalContext';
import { KEYS } from 'helpers/react-query.config';
import { CreateNewUseRequest } from 'api/security';
import { AxiosServiceError } from 'interfaces/general';

type Props = {
  editIdUser?: number;
  deleteId?: number;
};

const UseServiceSendFormUser = (props: Props) => {
  const { showApiError, showSuccess } = useGlobalContext();
  const { security: securityApi } = useApiContext();
  const queryClient = useQueryClient();
  const { t } = useTranslation('global');

  const [successSettingUser, setSuccessSettingUser] = useState();

  // GET USER DETAILS
  const { data: dataUserActive, isSuccess: userActiveDataSuccess } = useQuery(
    [KEYS.security.GetDetailsUser()],
    () => securityApi.accounts.GetDetailsUser(),
    {
      select: (data) => data?.data?.data,
      refetchOnWindowFocus: false,
      onError: (error) => showApiError(error, t('errorMessages.user'), 3000),
      onSuccess: (data) => data,
      cacheTime: Infinity
    }
  );

  // GET USER BY ID
  const {
    data: setDataUser,
    isSuccess: userDataSuccess,
    isLoading: isUserLoading
  } = useQuery(
    [KEYS.security.getUserById(props?.editIdUser)],
    () => securityApi.accounts.GetUserById(props?.editIdUser),
    {
      select: (data) => {
        const user = data?.data?.data;
        if (!user) return user;
        return {
          ...user,
          razonSocial: user.businessName ?? user.razonSocial
        };
      },
      enabled: !!props?.editIdUser,
      refetchOnWindowFocus: false,
      onError: (error) => showApiError(error, t('errorMessages.user'), 3000),
      onSuccess: (data) => data,
      cacheTime: 0
    }
  );

  // CREATE USER
  const createUsers = useMutation(
    (data: CreateNewUseRequest) => securityApi.accounts.CreateNewuser(data),
    {
      onError: (error) =>
        showApiError(
          error,
          t('common.commonMessages.genericRequestError'),
          3000
        ),
      onSuccess: () => {
        queryClient?.invalidateQueries(KEYS.security.getAllUsersByFilters());
      }
    }
  );

  // EDIT USER
  const editUser = useMutation(
    (data: EditUseRequest) => securityApi.accounts.EditUser(data),
    {
      onError: (error) =>
        showApiError(error, t('errorMessages.editUser'), 3000),
      onSuccess: (data) => {
        showSuccess(`${data?.data?.data?.message}`, 4000);
        queryClient?.invalidateQueries(KEYS.security.getAllUsersByFilters());
        queryClient?.invalidateQueries(
          KEYS.security.getUserById(props?.editIdUser)
        );
      }
    }
  );

  // DELETE USER
  const deleteUsers = useMutation(
    (idUser?: number) => securityApi.accounts.DeleteUsers(idUser),
    {
      onError: (error) =>
        showApiError(
          error,
          t('common.commonMessages.genericRequestError'),
          3000
        ),
      onSuccess: () => {
        showSuccess(t('users.successDelete'), 4000);
        queryClient?.invalidateQueries(KEYS.security.getAllUsersByFilters());
      }
    }
  );

  // SEND WHEREHOUSE USER
  const addWhereHouseUser = useMutation(
    (data?: AssignUserWarehouseRequest) =>
      securityApi.accounts.AssignUserWarehouse(data),
    {
      onError: (error: AxiosServiceError) => {
        showApiError(
          error,
          t('common.commonMessages.genericRequestError'),
          3000
        );
        setSuccessSettingUser(error);
      },
      onSuccess: async (data) => {
        showSuccess(t('success.warehousePerms'), 3000);
        setSuccessSettingUser(data);
        await queryClient?.invalidateQueries(
          KEYS.security.getUserWarehouseList(props?.editIdUser)
        );
      }
    }
  );

  // SEND SERVICES USER
  const addUserServices = useMutation(
    (data?: any) => securityApi.accounts.AssignUserServices(data),
    {
      onError: (error) => {
        showApiError(
          error,
          t('common.commonMessages.genericRequestError'),
          3000
        );
        setSuccessSettingUser(error);
      },
      onSuccess: (data) => {
        showSuccess(t('success.userServices'), 3000);
        setSuccessSettingUser(data);
        queryClient?.invalidateQueries([
          KEYS.security.getUserServicesList(props?.editIdUser)
        ]);
      }
    }
  );

  // SEND CLIENT USER
  const addClientUser = useMutation(
    (data?: any) => securityApi.accounts.AssignUserClients(data),
    {
      onError: (error) => {
        showApiError(
          error,
          t('common.commonMessages.genericRequestError'),
          3000
        );
        setSuccessSettingUser(error);
      },
      onSuccess: (data) => {
        showSuccess(t('success.userClients'), 3000);
        setSuccessSettingUser(data);
        queryClient?.invalidateQueries(
          KEYS.security.getUserClientsList(props?.editIdUser)
        );
      }
    }
  );

  // SEND WORKFORCE USER
  const addWorkForceUser = useMutation(
    (data?: any) => securityApi.accounts.AssignUserWorkForces(data),
    {
      onError: (error) => {
        showApiError(
          error,
          t('common.commonMessages.genericRequestError'),
          3000
        );
        setSuccessSettingUser(error);
      },
      onSuccess: (data) => {
        showSuccess(t('success.userTaskForces'), 3000);
        setSuccessSettingUser(data);
        queryClient?.invalidateQueries(
          KEYS.security.getUserWorkForcesList(props?.editIdUser)
        );
      }
    }
  );

  // SEND COMMENTYPE USER
  const addCommentTypeUser = useMutation(
    (data?: any) => securityApi.accounts.AssignUserTicketCommentType(data),
    {
      onError: (error) => {
        showApiError(
          error,
          t('common.commonMessages.genericRequestError'),
          3000
        );
        setSuccessSettingUser(error);
      },
      onSuccess: (data) => {
        showSuccess(t('success.userCommentTypes'), 3000);
        setSuccessSettingUser(data);
        queryClient?.invalidateQueries(
          KEYS.security.getUserTicketCommentTypeList(props?.editIdUser)
        );
      }
    }
  );

  // SEND COMMENTYPE USER
  const assignUserPermissionsGeneral = useMutation(
    (data: CloneUserAccessElement) =>
      securityApi.accounts.CloneUserAccessElement(data),
    {
      onError: (error) => {
        showApiError(
          error,
          t('common.commonMessages.genericRequestError'),
          3000
        );
      },
      onSuccess: () => {
        showSuccess(t('success.savedChanges'), 3000);
      }
    }
  );

  // SEND COMMENTYPE USER
  const addPermissionsGeneral = useMutation(
    (data?: any) => securityApi.accounts.AssignUserPermissionsGeneral(data),
    {
      onError: (error) => {
        showApiError(
          error,
          t('common.commonMessages.genericRequestError'),
          3000
        );
        setSuccessSettingUser(error);
      },
      onSuccess: (data) => {
        showSuccess(t('success.userGeneralPerms'), 3000);
        setSuccessSettingUser(data);
        queryClient?.invalidateQueries(
          KEYS.security.getUserTicketCommentTypeList()
        );
        queryClient?.invalidateQueries(KEYS.security.getAllUsersByFilters());
      }
    }
  );

  // UPDATE PERMITION USER
  const updatePermitionsUser = useMutation(
    (data?: any) => securityApi.accounts.updatePermitions(data),
    {
      onError: (error) => {
        showApiError(
          error,
          t('common.commonMessages.genericRequestError'),
          3000
        );
      },
      onSuccess: () => {
        showSuccess(t('success.userNewPerms'), 3000);
      }
    }
  );

  // GET USER PERMISSIONS
  const { data: getUserPermissions, isLoading: getUserPermissionsLoading } =
    useQuery(
      [KEYS.security.getUserPermitions(props?.editIdUser)],
      () => securityApi.accounts.GetUserPermission(props?.editIdUser),
      {
        select: (data) => data?.data?.data,
        enabled: !!props?.editIdUser,
        refetchOnWindowFocus: false,
        onError: (error) =>
          showApiError(error, t('errorMessages.userPerms'), 3000)
      }
    );

  const cloneUserAssignations = useMutation(
    (data: CloneUserAssignations) =>
      securityApi.accounts.CloneUserAssignations(data),
    {
      onError: (error) => {
        showApiError(
          error,
          t('common.commonMessages.genericRequestError'),
          3000
        );
        setSuccessSettingUser(error);
      },
      onSuccess: (data) => {
        showSuccess(t('success.copyInProgress'), 3000);
        setSuccessSettingUser(data);
        queryClient?.invalidateQueries([KEYS.security.getAssignationList()]);
      }
    }
  );

  return {
    dataUserActive,
    userActiveDataSuccess,
    setDataUser,
    userDataSuccess,
    isUserLoading,
    createUsers,
    editUser,
    deleteUsers,
    addWhereHouseUser,
    addClientUser,
    addWorkForceUser,
    addCommentTypeUser,
    addPermissionsGeneral,
    successSettingUser,
    updatePermitionsUser,
    getUserPermissions,
    getUserPermissionsLoading,
    addUserServices,
    assignUserPermissionsGeneral,
    cloneUserAssignations
  };
};

export default UseServiceSendFormUser;
